import { AnimatePresence } from 'framer-motion';
import subMonths from 'date-fns/subMonths';
import React, { ChangeEventHandler, useEffect, useMemo, useState } from 'react';
import { Range } from 'react-date-range';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { RowSelectionState } from '@tanstack/react-table';
import Datepicker from '../../components/atoms/Fields/Datepicker';
import { DatepickerPosition } from '../../components/atoms/Fields/Datepicker/enums';
import DataTable from '../../components/molecules/Table/dataTable';
import useHeaderTitleContext from '../../hooks/useHeaderTitleContext';
import { GetOrdersResponseSingleItem } from '../../setup/apiTypes/orders';
import apiRoutes from '../../setup/consts/apiRoutes';
import { ordersTableColumns } from './consts';
import {
  useExportOrders,
  useFailedReasonBreakdown,
  usePlaceholderProps,
  useBulkCancelOrders,
} from './hooks';
import {
  StyledActiveReasonPill,
  StyledBreakdownBarFill,
  StyledBreakdownBarTrack,
  StyledBreakdownCount,
  StyledBreakdownHeader,
  StyledBreakdownHint,
  StyledBreakdownPct,
  StyledBreakdownRow,
  StyledBreakdownRows,
  StyledBreakdownSection,
  StyledShowLessButton,
  StyledBreakdownSkeleton,
  StyledBreakdownSkeletonRow,
  StyledBreakdownTitle,
  StyledBreakdownReasonBlock,
  StyledBreakdownReasonAr,
  StyledBreakdownReasonEn,
  StyledStatGrid,
  StyledStatLabel,
  StyledStatValue,
  StyledStatUnit,
  StyledStatSub,
  StyledCityBadgesRow,
  StyledCityBadgePill,
  StyledForm,
  StyledTableWrapper,
  StyledTrendBadge,
  StyledInfoTooltipWrapper,
  StyledBulkBar,
  StyledBulkBarLabel,
  StyledBulkCancelButton,
  StyledSkeletonPill,
} from './styles';
import {
  StyledToolbar,
  StyledToolbarActions,
  StyledToolbarFilters,
  StyledToolbarGhostButton,
  StyledToolbarSearch,
} from '../../components/molecules/Toolbar';
import OrderSummaryModal from '../../components/organisms/Modal/templates/OrderSummaryModal';
import useToggle from '../../hooks/useToggle';
import { DataTableAPI } from '../../components/molecules/Table/types';
import { utcToLocal } from '../../utils/date';
import ConfirmationModal from '../../components/organisms/Modal/templates/ConfirmationModal';
import Input from '../../components/atoms/Fields/Input';
import { FieldMode } from '../../components/atoms/Fields/enums';
import { IconClose, IconDownload, IconInfo, IconSearch } from '../../assets/svg/icons';
import Spinner from '../../components/atoms/Spinner';
import { SpinnerMode, SpinnerSize } from '../../components/atoms/Spinner/enums';
import { StyledClearButton } from '../../components/atoms/Fields/Autocomplete/styles';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import ScCard from '../../components/atoms/ScCard';
import Button from '../../components/atoms/Button';
import { ButtonKind } from '../../components/atoms/Button/enums';

const REASON_TIPS: Record<string, string> = {
  'لا يرد':
    "Customer isn't answering. Try WhatsApp or call at a different time (early morning or evening work best).",
  'لا يرد بعد الاتفاق':
    "Customer agreed but stopped responding. Send a WhatsApp reminder with the delivery window and driver's name.",
  'رفض من الزبون':
    'Customer refused on delivery. Usually signals a product mismatch — check your product images and descriptions match reality.',
  'الطلب غير مطابق':
    "Product didn't match the order. Audit your pack process — wrong item shipped or quantity wrong?",
};

const REASON_TIPS_EN: Record<string, string> = {
  'لا يرد': 'No answer',
  'لا يرد بعد الاتفاق': 'No answer after agreement',
  'رفض من الزبون': 'Refused by customer',
  'الطلب غير مطابق': 'Order mismatch',
  'العنوان خاطئ': 'Wrong address',
  'مؤجل': 'Postponed',
  'ملغي': 'Cancelled',
};

const GENERIC_TIP =
  'Review these orders one by one and either re-place or cancel to clear the backlog.';

const defaultRange = {
  startDate: subMonths(new Date(), 1),
  endDate: new Date(),
};

const FailedOrdersPage: React.FC = () => {
  const [range, setRange] = useState<Range>(defaultRange);
  const [detailsOrderId, setDetailsOrderId] = useState<string>();
  const [ordersTableAPI, setOrdersTableAPI] = useState<DataTableAPI>();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [showAllReasons, setShowAllReasons] = useState(false);
  const [valueMode, setValueMode] = useState<'count' | 'iqd'>('count');

  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();
  const tableColumns = useMemo(() => ordersTableColumns(intl), [intl]);
  const { title, description, Icon } = usePlaceholderProps();

  const [debouncedQuery] = useDebounce(searchQuery, 300);
  const { handleDownloadOrdersExcelFile, isLoading: exportLoading } =
    useExportOrders();
  const { isLoading: bulkCancelLoading, bulkCancel } = useBulkCancelOrders();
  const [
    exportConfirmationModalIsOpen,
    { toggle: toggleExportConfirmationModal },
  ] = useToggle();

  const selectedOrderIds = Object.keys(rowSelection).filter(
    (k) => rowSelection[k]
  );
  const selectedCount = selectedOrderIds.length;

  const { breakdown, isLoading: breakdownLoading } = useFailedReasonBreakdown({
    from: range.startDate,
    to: range.endDate,
  });

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'mainNavigation.failedOrders',
        defaultMessage: 'Failed Orders',
      })
    );
  }, [intl, setHeaderTitle]);

  // Reset reason filter when date range changes.
  useEffect(() => {
    setSelectedReason(null);
  }, [range.startDate, range.endDate]);

  const handleSearchQueryChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearchQuery = () => setSearchQuery('');

  const onClearFiltersButtonClick = () => {
    clearSearchQuery();
    setSelectedReason(null);
    setRange(defaultRange);
  };

  const onExport = () => {
    if (exportConfirmationModalIsOpen) toggleExportConfirmationModal();
    handleDownloadOrdersExcelFile({
      statuses: ['Failed'],
      from: range.startDate && utcToLocal(range.startDate).toISOString(),
      to: range.endDate && utcToLocal(range.endDate).toISOString(),
      page: ordersTableAPI?.page || 1,
      pageSize: ordersTableAPI?.pageSize || 10,
      search: debouncedQuery,
    });
  };


  const failedCount = ordersTableAPI?.totalCount;

  const hasActiveFilters =
    range.startDate?.toDateString() !== defaultRange.startDate.toDateString() ||
    range.endDate?.toDateString() !== defaultRange.endDate.toDateString() ||
    !!debouncedQuery ||
    !!selectedReason;

  return (
    <>
      <ScPageHeader
        eyebrow={intl.formatMessage({ id: 'failedOrders.heroEyebrow', defaultMessage: 'REVENUE RECOVERY' })}
        title={
          failedCount !== undefined
            ? intl.formatMessage({ id: 'failedOrders.heroTitle', defaultMessage: '{count} need attention' }, { count: failedCount.toLocaleString() })
            : intl.formatMessage({ id: 'mainNavigation.failedOrders', defaultMessage: 'Failed Orders' })
        }
        subtitle={intl.formatMessage({ id: 'failedOrders.heroDescription', defaultMessage: 'Each failed order is revenue waiting to be recovered. Click any reason to filter, then re-place or cancel — in seconds.' })}
        actions={
          <Button
            kind={ButtonKind.Outline}
            icon={IconDownload}
            onClick={() => {
              if (ordersTableAPI?.totalCount && ordersTableAPI.totalCount > 2000) {
                toggleExportConfirmationModal();
                return;
              }
              onExport();
            }}
            disabled={exportLoading || ordersTableAPI?.isEmpty}
          >
            <FormattedMessage id='downloadXl' defaultMessage='Export' />
          </Button>
        }
      />

      {exportConfirmationModalIsOpen ? (
        <ConfirmationModal
          title={intl.formatMessage({ id: 'exportConfirmation' })}
          confirmButtonLabel={intl.formatMessage({ id: 'export' })}
          description={intl.formatMessage({
            id: 'ordersExportConfirmationDescription',
          })}
          cancelButtonLabel={intl.formatMessage({ id: 'cancel' })}
          withCloseButton
          onConfirmButtonClick={onExport}
          onClose={toggleExportConfirmationModal}
          loadingConfirm={exportLoading}
        />
      ) : null}

      {bulkConfirmOpen ? (
        <ConfirmationModal
          title={intl.formatMessage({ id: 'failedOrders.cancelSelectedOrders', defaultMessage: 'Cancel selected orders' })}
          description={intl.formatMessage({ id: 'failedOrders.cancelConfirmation', defaultMessage: 'Are you sure you want to cancel {count} order(s)? This action cannot be undone.' }, { count: selectedCount })}
          confirmButtonLabel={intl.formatMessage({ id: 'failedOrders.cancelOrders', defaultMessage: 'Cancel orders' })}
          cancelButtonLabel={intl.formatMessage({ id: 'failedOrders.goBack', defaultMessage: 'Go back' })}
          isWarning
          withCloseButton
          loadingConfirm={bulkCancelLoading}
          onConfirmButtonClick={() => {
            bulkCancel(selectedOrderIds, () => {
              setBulkConfirmOpen(false);
              setRowSelection({});
              ordersTableAPI?.refetch();
            });
          }}
          onClose={() => setBulkConfirmOpen(false)}
        />
      ) : null}


      {/* Hero stat trio */}
      <StyledStatGrid>
        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <StyledStatLabel>{intl.formatMessage({ id: 'failedOrders.recoverableRevenue', defaultMessage: 'Recoverable Revenue' })}</StyledStatLabel>
          <StyledStatValue $danger>
            {failedCount !== undefined ? failedCount.toLocaleString() : '—'}
            {failedCount !== undefined && (
              <StyledStatUnit>{intl.formatMessage({ id: 'failedOrders.orders', defaultMessage: 'orders' })}</StyledStatUnit>
            )}
          </StyledStatValue>
          <StyledStatSub>{intl.formatMessage({ id: 'failedOrders.failedOrdersPendingRecovery', defaultMessage: 'Failed orders pending recovery this period.' })}</StyledStatSub>
        </ScCard>

        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <StyledStatLabel>{intl.formatMessage({ id: 'failedOrders.medianTimeToFail', defaultMessage: 'Median Time to Fail' })}</StyledStatLabel>
          <StyledStatValue $danger={false}>—</StyledStatValue>
          <StyledStatSub>{intl.formatMessage({ id: 'failedOrders.medianTimeToFailSub', defaultMessage: 'From placed to flagged as failed.' })}</StyledStatSub>
        </ScCard>

        <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
          <StyledStatLabel>{intl.formatMessage({ id: 'failedOrders.failureRateByCity', defaultMessage: 'Failure Rate by City · Top 4' })}</StyledStatLabel>
          <StyledCityBadgesRow>
            {breakdown.length > 0 ? (
              breakdown.slice(0, 4).map((r) => (
                <StyledCityBadgePill
                  key={r.reason}
                  type='button'
                  $active={selectedReason === r.reason}
                  onClick={() => setSelectedReason((prev) => (prev === r.reason ? null : r.reason))}
                >
                  <span>{r.reason}</span>
                  <span style={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{r.pct}%</span>
                </StyledCityBadgePill>
              ))
            ) : breakdownLoading ? (
              <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{intl.formatMessage({ id: 'dashboard.loading', defaultMessage: 'Loading…' })}</span>
            ) : (
              <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{intl.formatMessage({ id: 'failedOrders.noDataForPeriod', defaultMessage: 'No data for this period' })}</span>
            )}
          </StyledCityBadgesRow>
        </ScCard>
      </StyledStatGrid>

      {/* Failure reason breakdown — always rendered; skeleton while loading */}
      <StyledBreakdownSection>
        <StyledBreakdownHeader>
          <StyledBreakdownTitle>{intl.formatMessage({ id: 'failedOrders.whyAreOrdersFailing', defaultMessage: 'Why are orders failing?' })}</StyledBreakdownTitle>
          {!breakdownLoading && breakdown.length > 0 && (
            <StyledBreakdownHint>{intl.formatMessage({ id: 'failedOrders.clickReasonHint', defaultMessage: 'Click a reason to filter the table' })}</StyledBreakdownHint>
          )}
        </StyledBreakdownHeader>

        {breakdownLoading ? (
          <StyledBreakdownSkeleton>
            {[100, 65, 40, 25].map((w) => (
              <StyledBreakdownSkeletonRow key={w}>
                <StyledSkeletonPill width={`${w}%`} />
                <StyledSkeletonPill />
                <StyledSkeletonPill width='2rem' />
                <StyledSkeletonPill width='2.5rem' />
              </StyledBreakdownSkeletonRow>
            ))}
          </StyledBreakdownSkeleton>
        ) : breakdown.length === 0 ? (
          <StyledBreakdownHint>{intl.formatMessage({ id: 'failedOrders.noBreakdownData', defaultMessage: 'No failure reason data for this period.' })}</StyledBreakdownHint>
        ) : (() => {
          const TOP = 5;
          const visible = showAllReasons ? breakdown : breakdown.slice(0, TOP);
          const rest = breakdown.slice(TOP);
          const otherCount = rest.reduce((s, r) => s + r.count, 0);
          const otherPct = rest.reduce((s, r) => s + r.pct, 0);
          return (
            <StyledBreakdownRows>
              {visible.map(({ reason, count, pct, trend }) => {
                const englishLabel = REASON_TIPS_EN[reason];
                return (
                  <StyledBreakdownRow
                    key={reason}
                    active={selectedReason === reason}
                    onClick={() =>
                      setSelectedReason((prev) => (prev === reason ? null : reason))
                    }
                  >
                    <StyledBreakdownReasonBlock>
                      <StyledBreakdownReasonAr className='ar' title={reason} $active={selectedReason === reason}>{reason}</StyledBreakdownReasonAr>
                      {englishLabel && intl.locale !== 'ar' && (
                        <StyledBreakdownReasonEn title={englishLabel}>{englishLabel}</StyledBreakdownReasonEn>
                      )}
                    </StyledBreakdownReasonBlock>
                    <StyledBreakdownBarTrack>
                      <StyledBreakdownBarFill pct={pct} />
                    </StyledBreakdownBarTrack>
                    <StyledBreakdownCount>
                      {count}
                      {trend !== 'stable' && (
                        <StyledTrendBadge trend={trend}>
                          {trend === 'up' ? '↑' : '↓'}
                        </StyledTrendBadge>
                      )}
                    </StyledBreakdownCount>
                    <StyledBreakdownPct>{pct}%</StyledBreakdownPct>
                    <StyledInfoTooltipWrapper
                      data-tip={REASON_TIPS[reason] ?? GENERIC_TIP}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <IconInfo />
                    </StyledInfoTooltipWrapper>
                  </StyledBreakdownRow>
                );
              })}

              {!showAllReasons && rest.length > 0 && (
                <StyledBreakdownRow
                  as='div'
                  active={false}
                  onClick={() => setShowAllReasons(true)}
                  style={{ cursor: 'pointer', opacity: 0.7 }}
                >
                  <StyledBreakdownReasonBlock>
                    <StyledBreakdownReasonEn>{intl.formatMessage({ id: 'failedOrders.moreReasons', defaultMessage: '+{count} more reasons' }, { count: rest.length })}</StyledBreakdownReasonEn>
                  </StyledBreakdownReasonBlock>
                  <StyledBreakdownBarTrack>
                    <StyledBreakdownBarFill pct={otherPct} />
                  </StyledBreakdownBarTrack>
                  <StyledBreakdownCount>{otherCount}</StyledBreakdownCount>
                  <StyledBreakdownPct>{otherPct}%</StyledBreakdownPct>
                </StyledBreakdownRow>
              )}

              {showAllReasons && rest.length > 0 && (
                <StyledShowLessButton
                  type='button'
                  onClick={() => setShowAllReasons(false)}
                >
                  {intl.formatMessage({ id: 'failedOrders.showLess', defaultMessage: 'Show less' })}
                </StyledShowLessButton>
              )}
            </StyledBreakdownRows>
          );
        })()}
      </StyledBreakdownSection>

      <ScCard pad={false} style={{ padding: 'var(--s-4)', marginBottom: 'var(--s-4)' }}>
      <StyledToolbar style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
        <StyledToolbarSearch>
          <StyledForm onSubmit={(e) => e.preventDefault()}>
            <Input
              mode={FieldMode.Tertiary}
              placeholder={intl.formatMessage({
                id: 'search.searchOrders',
                defaultMessage: 'Search orders…',
              })}
              isRounded
              prefixComponent={<IconSearch />}
              suffixComponent={
                (ordersTableAPI?.loading && searchQuery) ||
                (debouncedQuery && !searchQuery) ? (
                  <Spinner
                    mode={SpinnerMode.Dark}
                    size={SpinnerSize.ExtraSmall}
                  />
                ) : debouncedQuery ? (
                  <StyledClearButton type='button' aria-label='Clear search' onClick={clearSearchQuery}>
                    <IconClose />
                  </StyledClearButton>
                ) : null
              }
              value={searchQuery}
              onChange={handleSearchQueryChange}
              type='input'
              disabled={ordersTableAPI?.loading}
            />
          </StyledForm>
        </StyledToolbarSearch>

        <StyledToolbarFilters>
          {/* Active reason pill — shows which reason is filtering the table */}
          {selectedReason && (
            <StyledActiveReasonPill
              type='button'
              onClick={() => setSelectedReason(null)}
              title='Clear reason filter'
            >
              {selectedReason}
              <IconClose />
            </StyledActiveReasonPill>
          )}

          <Datepicker
            value={range}
            onChange={(item) => {
              setRange(item);
            }}
            position={DatepickerPosition.Right}
          />

          {hasActiveFilters && (
            <StyledToolbarGhostButton
              type='button'
              onClick={onClearFiltersButtonClick}
            >
              <IconClose />
              <FormattedMessage
                id='search.clearFilters'
                defaultMessage='Clear'
              />
            </StyledToolbarGhostButton>
          )}
        </StyledToolbarFilters>

        <StyledToolbarActions>
          {exportLoading && (
            <Spinner mode={SpinnerMode.Dark} size={SpinnerSize.ExtraSmall} />
          )}
          {/* Counts / IQD value segmented toggle */}
          <div style={{
            display: 'inline-flex',
            borderRadius: 'var(--r-sm)',
            border: '1px solid var(--line-2)',
            overflow: 'visible',
            flexShrink: 0,
          }}>
            <button
              type='button'
              aria-pressed={true}
              style={{
                padding: '0 var(--s-3)',
                height: 32,
                background: 'var(--brand-ink)',
                color: 'var(--surface)',
                border: 0,
                fontFamily: 'inherit',
                fontSize: 'var(--t-sm)',
                fontWeight: 600,
                cursor: 'default',
                borderRadius: 'var(--r-sm) 0 0 var(--r-sm)',
              }}
            >
              {intl.formatMessage({ id: 'failedOrders.viewCounts', defaultMessage: 'Counts' })}
            </button>
            <div
              title={intl.formatMessage({ id: 'comingSoon', defaultMessage: 'Coming soon' })}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                padding: '0 var(--s-3)',
                height: 32,
                background: 'var(--surface)',
                color: 'var(--muted-2)',
                border: 0,
                fontFamily: 'inherit',
                fontSize: 'var(--t-sm)',
                fontWeight: 400,
                cursor: 'not-allowed',
                gap: 6,
                borderRadius: '0 var(--r-sm) var(--r-sm) 0',
              }}
            >
              {intl.formatMessage({ id: 'failedOrders.viewIqdValue', defaultMessage: 'IQD value' })}
              <span style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                background: 'var(--paper-2)',
                color: 'var(--muted)',
                border: '1px solid var(--line-2)',
                borderRadius: 'var(--r-sm)',
                padding: '1px 5px',
                lineHeight: 1.4,
              }}>
                {intl.formatMessage({ id: 'comingSoon.short', defaultMessage: 'Soon' })}
              </span>
            </div>
          </div>
        </StyledToolbarActions>
      </StyledToolbar>
      </ScCard>

      {selectedCount > 0 && (
        <StyledBulkBar>
          <StyledBulkBarLabel>
            {intl.formatMessage({ id: 'failedOrders.ordersSelected', defaultMessage: '{count} orders selected' }, { count: selectedCount })}
          </StyledBulkBarLabel>
          <StyledBulkCancelButton
            type='button'
            disabled={bulkCancelLoading}
            onClick={() => setBulkConfirmOpen(true)}
          >
            {intl.formatMessage({ id: 'failedOrders.cancelSelected', defaultMessage: 'Cancel selected' })}
          </StyledBulkCancelButton>
        </StyledBulkBar>
      )}

      <ScCard pad={false}>
      <StyledTableWrapper>
        <DataTable<GetOrdersResponseSingleItem>
          endpoint={apiRoutes.ORDERS}
          tableAPIRef={setOrdersTableAPI}
          placeholderProps={{ title, description, icon: Icon }}
          columns={tableColumns}
          rowSelection={rowSelection}
          setSelectedRows={setRowSelection}
          getRowId={(row) => row.id}
          query={{
            ...(range.startDate
              ? { from: utcToLocal(range.startDate).toISOString() }
              : {}),
            ...(range.endDate
              ? { to: utcToLocal(range.endDate).toISOString() }
              : {}),
            ...(debouncedQuery ? { search: debouncedQuery } : {}),
            ...(selectedReason ? { failureReason: selectedReason } : {}),
            statuses: ['Failed'],
          }}
          onRowClick={({ original }) => setDetailsOrderId(original.id)}
          queryOptions={{ cacheTime: 0 }}
        />
        <AnimatePresence>
          {detailsOrderId ? (
            <OrderSummaryModal
              orderId={detailsOrderId}
              refetchOrders={ordersTableAPI?.refetch}
              onClose={() => setDetailsOrderId(undefined)}
            />
          ) : null}
        </AnimatePresence>
      </StyledTableWrapper>
      </ScCard>
    </>
  );
};

export default FailedOrdersPage;
